// Where a Relatude.DB installation sends someone to be given a license. The installation made the
// pairing itself, with no credentials at all - it has none yet - and is polling for an answer. This
// page is where the answer comes from, and it is the only step in the flow that carries any
// authority: a signed-in owner choosing which of their licenses the installation may have.
//
// So the page is deliberately explicit about which installation is asking. Anyone can start a
// pairing and send its url to anyone; what stops that mattering is that a person has to read this
// page and agree, and that the keys only ever go back to whoever started it.
import { useEffect, useState } from 'react'
import { errorMessage, formatDate, pairing, portal, type LicenseSummary, type PairingInfo, type TemplateInfo } from '../api'
import { useAuth } from '../auth'
import { AuthLayout } from '../components/AuthLayout'
import { SignInMethods } from '../components/SignInMethods'
import { Badge, ErrorText, Facts, Field, Icon, Link, Notice, Spinner } from '../components/Ui'
import { currentUrl, useRoute } from '../router'

export function PairPage() {
  const route = useRoute()
  const pairingId = route.query.get('id') ?? ''
  const { user, providers, setUser } = useAuth()
  const [info, setInfo] = useState<PairingInfo | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  useEffect(() => {
    if (!pairingId) {
      setLoadError('This page needs a pairing from a Relatude.DB installation. Open the License page there and choose "Create a license".')
      return
    }
    let cancelled = false
    pairing
      .describe(pairingId)
      .then((r) => {
        if (!cancelled) setInfo(r)
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(errorMessage(err))
      })
    return () => {
      cancelled = true
    }
  }, [pairingId])

  return (
    <AuthLayout
      title="Giving an installation a license."
      lead="A Relatude.DB installation is asking for a license. Choose one of yours or make a new one, and its keys go straight back to the installation that asked - you never have to copy them."
      points={[
        {
          icon: 'shield',
          title: 'Only you can answer it',
          text: 'The installation could not give itself a license: it has no keys yet. It takes a signed-in owner choosing one, which is what you are about to do.',
        },
        {
          icon: 'key',
          title: 'A new API key, every time',
          text: 'An existing license is given a fresh API key rather than one already in use somewhere else, so this installation can be shut out on its own later.',
        },
        {
          icon: 'server',
          title: 'Check what is asking',
          text: 'The installation below is whoever started this pairing. If you did not just do that yourself on that machine, close this page.',
        },
      ]}
    >
      {loadError ? (
        <>
          <h1 className="panel-title">Pairing</h1>
          <p className="panel-lead">The link this page was opened with cannot be used.</p>
          <ErrorText error={loadError} />
          <p className="signin-footnote">
            Pairings last a few minutes. Start again from the installation's License page, or <Link to="/">go to your licenses</Link>.
          </p>
        </>
      ) : !info ? (
        <Spinner label="Looking up the pairing…" />
      ) : done ? (
        <>
          <h1 className="panel-title">Done</h1>
          <p className="panel-lead">
            The installation has been given <strong>{done}</strong>. It is collecting the keys now, and its License page will say so within a few seconds.
          </p>
          <Notice kind="success">You can close this page.</Notice>
          <p className="signin-footnote">
            <Link to="/">Your licenses</Link>
          </p>
        </>
      ) : info.completed ? (
        <>
          <h1 className="panel-title">Already answered</h1>
          <p className="panel-lead">
            This pairing was given {info.licenseName ? <strong>{info.licenseName}</strong> : 'a license'} already. If that was not you, start a new pairing
            from the installation.
          </p>
          <p className="signin-footnote">
            <Link to="/">Your licenses</Link>
          </p>
        </>
      ) : (
        <>
          <h1 className="panel-title">{info.installationName || 'An installation'} wants a license</h1>
          <p className="panel-lead">
            {user ? 'Choose the license it should run under.' : 'Sign in with your Relatude Cloud account, then choose the license it should run under.'}
          </p>
          <div className="request">
            <div className="request-name">
              <Icon name="server" />
              <span>{info.installationName || 'Unnamed installation'}</span>
              <Badge tone="warn">waiting</Badge>
            </div>
            <Facts
              items={[
                { label: 'Installation', value: <code>{info.installationKey}</code> },
                { label: 'Expires', value: formatDate(info.expiresUtc) },
              ]}
            />
          </div>
          {!user ? (
            <SignInMethods providers={providers} returnUrl={currentUrl()} onSignedIn={(result) => setUser(result.user)} />
          ) : (
            <ChooseLicense pairingId={info.pairingId} onDone={setDone} />
          )}
        </>
      )}
    </AuthLayout>
  )
}

/** The choice itself: one of the user's licenses, or a new one from a product bundle. */
function ChooseLicense({ pairingId, onDone }: { pairingId: string; onDone: (licenseName: string) => void }) {
  const [licenses, setLicenses] = useState<LicenseSummary[] | null>(null)
  const [templates, setTemplates] = useState<TemplateInfo[]>([])
  const [choice, setChoice] = useState<string>('new')
  const [templateId, setTemplateId] = useState<string>('')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([portal.licenses(), portal.templates()])
      .then(([l, t]) => {
        setLicenses(l)
        setTemplates(t)
        setTemplateId(t[0]?.id ?? '')
        // somebody with licenses already is more likely to be adding an installation to one than
        // making their fifth; somebody with none can only make one
        if (l.length > 0) setChoice(l[0].id)
      })
      .catch((err: unknown) => setError(errorMessage(err)))
  }, [])

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      const result =
        choice === 'new'
          ? await pairing.complete(pairingId, { templateId: templateId || null, name: name.trim() || undefined })
          : await pairing.complete(pairingId, { licenseId: choice })
      onDone(result.licenseName)
    } catch (err) {
      setError(errorMessage(err))
      setBusy(false)
    }
  }

  if (licenses === null && !error) return <Spinner label="Loading your licenses…" />

  return (
    <div className="signin">
      <ErrorText error={error} />
      {licenses && licenses.length > 0 && (
        <Field label="License">
          <select value={choice} onChange={(e) => setChoice(e.target.value)}>
            {licenses.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
                {l.disabled ? ' (disabled)' : l.expired ? ' (expired)' : ''}
              </option>
            ))}
            <option value="new">Make a new license…</option>
          </select>
        </Field>
      )}
      {choice === 'new' && (
        <>
          <Field label="Product bundle" hint={templates.length === 0 ? 'No bundles are offered yet; the license starts without entitlements.' : undefined}>
            <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
              <option value="">No product bundle (bare license)</option>
            </select>
          </Field>
          <Field label="Name" hint="For you: the site or project this installation is.">
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="My site" />
          </Field>
        </>
      )}
      <button type="button" className="button button-primary" disabled={busy} onClick={submit}>
        {busy ? 'Giving it the license…' : choice === 'new' ? 'Create the license and hand it over' : 'Give the installation this license'}
      </button>
      <p className="signin-footnote">
        The installation is given a new API key of its own either way, so you can shut it out later without touching anything else that uses the license.
      </p>
    </div>
  )
}
